/**
 * sector-context.ts
 * ─────────────────────────────────────────────────────────────────
 * Contenu éditorial conditionnel par secteur CEE pour la page fiche.
 *
 * Chaque secteur (BAR, BAT, IND, AGRI, TRA, RES) reçoit :
 *  - Une FAQ courte de 2-3 questions spécifiques.
 *  - Une liste de pièces justificatives typiques pour ce secteur.
 *  - Un bandeau audience qui pousse vers l'espace adapté.
 *
 * L'idée : éviter d'avoir 200 pages de fiches CEE qui se ressemblent
 * toutes — donner une couche éditoriale spécifique au secteur sans
 * dépendre de l'enrichissement par fiche en base.
 */

export type CeeSector = 'BAR' | 'BAT' | 'IND' | 'AGRI' | 'TRA' | 'RES';

export interface SectorFaqItem {
  question: string;
  answer: string;
}

export interface SectorAudienceLink {
  /** Label de l'eyebrow ("Pour les particuliers"). */
  eyebrow: string;
  /** Titre principal du bandeau. */
  title: string;
  /** Description courte (1 phrase). */
  description: string;
  /** CTA principal (vers l'espace audience ou un outil). */
  primaryCta: { label: string; href: string };
  /** CTA secondaire (vers une page d'aide ou le hub). */
  secondaryCta?: { label: string; href: string };
}

export interface SectorContext {
  /** Audience cross-link contextualisé. */
  audience: SectorAudienceLink;
  /** 2-3 questions FAQ communes à toutes les fiches du secteur. */
  faq: SectorFaqItem[];
  /** Pièces justificatives spécifiques (en plus des pièces génériques). */
  extraDocs: string[];
}

// ────────────────────────────────────────────────────────────────
// BAR — Bâtiment résidentiel (particuliers)
// ────────────────────────────────────────────────────────────────

const BAR: SectorContext = {
  audience: {
    eyebrow: 'Vous êtes particulier ?',
    title: "Cette prime se cumule avec MaPrimeRénov’",
    description:
      "Propriétaire occupant ou bailleur, vous additionnez la prime CEE (versée par les fournisseurs d'énergie) avec MaPrimeRénov’ (versée par l'Anah), l'éco-PTZ et la TVA à 5,5 %. On chiffre l'ensemble en quelques minutes.",
    primaryCta: { label: 'Calculer toutes mes aides', href: '/particuliers/calculer-mes-aides' },
    secondaryCta: { label: 'Comprendre chaque aide', href: '/particuliers/aides' },
  },
  faq: [
    {
      question: "Quelle différence avec MaPrimeRénov\u2019 ?",
      answer:
        "MaPrimeRénov’ est versée par l'Anah, sous conditions de revenus. La prime CEE (ou « prime énergie ») est versée par les fournisseurs d'énergie, sans plafond de ressources. Les deux se cumulent sur un même chantier, dans la limite d'un reste à charge minimum fixé par la loi.",
    },
    {
      question: "Faut-il un artisan RGE ?",
      answer:
        "Oui, sans exception. Les travaux résidentiels valorisés en CEE doivent être réalisés par un artisan certifié RGE dans le domaine concerné (QualiPAC, Qualibat RGE, etc.). Si la certification n'est pas active à la date du devis, la prime tombe.",
    },
    {
      question: "Quand faire la demande de prime ?",
      answer:
        "Avant la signature du devis, sans exception. Le dossier CEE se construit en deux temps : engagement avant devis signé, puis dépôt après travaux avec factures et attestations. Si vous engagez la prime après devis signé, elle est rejetée.",
    },
  ],
  extraDocs: [
    "Avis d'imposition pour évaluer le niveau de ressources (CEE classique, sans bonification précarité)",
    'Justificatif de propriété ou bail (résidence principale)',
    'Attestation RGE active à la date du devis',
  ],
};

// ────────────────────────────────────────────────────────────────
// BAT — Bâtiment tertiaire (entreprises, collectivités, tertiaire)
// ────────────────────────────────────────────────────────────────

const BAT: SectorContext = {
  audience: {
    eyebrow: 'Vous êtes propriétaire ou exploitant tertiaire ?',
    title: 'Cette opération peut financer votre trajectoire OPERAT',
    description:
      "Les fiches BAT (bureaux, commerces, hôtels, ERP) entrent dans votre trajectoire \u221240 % à 2030. Cumulables avec le suramortissement art. 39 quinquies et certains prêts Bpifrance climat.",
    primaryCta: { label: "Auditer mon Décret Tertiaire", href: '/tertiaire/decret-tertiaire' },
    secondaryCta: { label: 'Voir nos solutions pro', href: '/professionnels' },
  },
  faq: [
    {
      question: 'Cette fiche est-elle valorisable dans mon plan OPERAT ?',
      answer:
        "Oui, dès lors que l'opération améliore les consommations du bâtiment déclaré sur OPERAT. Le gain de la fiche alimente directement votre trajectoire \u221240 / \u221250 / \u221260 % à 2030 / 2040 / 2050, à condition de respecter les exigences techniques de la fiche.",
    },
    {
      question: 'Peut-on cumuler avec le suramortissement art. 39 quinquies ?',
      answer:
        "Oui. Le suramortissement fiscal (40 % du prix de revient sur certains équipements de production de froid, d'éclairage LED, de chaudières biomasse…) se cumule avec la prime CEE et reste hors des règles d'aides d'État européennes.",
    },
    {
      question: 'Faut-il être propriétaire pour porter le dossier ?',
      answer:
        "Non. Le bénéficiaire CEE peut être le propriétaire bailleur, l'exploitant locataire (si le bail le prévoit) ou le syndicat des copropriétaires pour les parties communes tertiaires.",
    },
  ],
  extraDocs: [
    'Extrait Kbis du bénéficiaire CEE',
    'Justificatif de propriété ou bail commercial avec clause travaux',
    'Note de calcul OPERAT prévisionnelle (recommandé)',
  ],
};

// ────────────────────────────────────────────────────────────────
// IND — Industrie (sites de production)
// ────────────────────────────────────────────────────────────────

const IND: SectorContext = {
  audience: {
    eyebrow: 'Vous êtes industriel ?',
    title: 'Cumulable avec Fonds chaleur et décarbonation',
    description:
      "Les fiches IND-UT (utilités) et IND-BA (bâti industriel) s'additionnent au Fonds chaleur ADEME, au Fonds décarbonation industrie et aux prêts Bpifrance climat, dans la limite des plafonds européens d'aides d'État.",
    primaryCta: { label: 'Diagnostic CEE industriel', href: '/industrie/diagnostic-cee' },
    secondaryCta: { label: 'Voir nos solutions industrie', href: '/industrie' },
  },
  faq: [
    {
      question: "L'audit énergétique réglementaire est-il obligatoire ?",
      answer:
        "Pour les grandes entreprises (≥ 250 salariés ou ≥ 50 M\u20ac CA et ≥ 43 M\u20ac bilan), oui : audit NF EN 16247-1 tous les 4 ans. Sauf si vous êtes certifié ISO 50001 sur au moins 80 % de votre périmètre énergétique.",
    },
    {
      question: 'Quels Coups de pouce IND sont en vigueur ?',
      answer:
        "Plusieurs Coups de pouce industrie peuvent bonifier la prime de base (régulation des moteurs, isolation des points singuliers, récupération de chaleur fatale\u2026). Les bonifications évoluent au gré des arrêtés. On vérifie ce qui s'applique à votre opération à la date du dépôt.",
    },
    {
      question: 'En cas de contrôle DREAL, qui prend la main ?',
      answer:
        "On le gère pour vous, en tant que mandataire CEE : sur place ou par contact. On garde l'ensemble des pièces 9 ans et on apporte l'expertise technique en cas de suites contradictoires.",
    },
  ],
  extraDocs: [
    "Rapport d'audit énergétique NF EN 16247-1 (si grande entreprise)",
    "Note de calcul du Bureau d\u2019\u00c9tudes thermiques",
    'Schéma technique de l\u2019installation avant/après',
  ],
};

// ────────────────────────────────────────────────────────────────
// AGRI — Exploitations agricoles
// ────────────────────────────────────────────────────────────────

const AGRI: SectorContext = {
  audience: {
    eyebrow: 'Vous êtes exploitant agricole ?',
    title: 'CEE agricoles + PCAE régional, ça se cumule',
    description:
      "Les fiches AGRI couvrent vos bâtiments d'élevage, vos serres, vos hangars et vos utilités agricoles. Cumulables avec le PCAE de votre Région et les aides nationales FranceAgriMer.",
    primaryCta: { label: 'Lancer le diagnostic agricole', href: '/agriculteurs/diagnostic-cee' },
    secondaryCta: { label: 'Aides agricoles', href: '/agriculteurs/aides' },
  },
  faq: [
    {
      question: 'Le PCAE de ma Région se cumule-t-il avec les CEE ?',
      answer:
        "Oui, dans la majorité des cas. Le PCAE (Plan de Compétitivité et d'Adaptation des Exploitations) est financé par votre Région, indépendamment des CEE. On vérifie les règles régionales et les plafonds de cumul éventuels lors du diagnostic.",
    },
    {
      question: "Mon logement de fonction est-il \u00e9ligible \u00e0 MaPrimeR\u00e9nov\u2019 ?",
      answer:
        "Oui s'il est votre r\u00e9sidence principale. MaPrimeR\u00e9nov\u2019 couvre uniquement le logement r\u00e9sidentiel, pas les hangars ni les b\u00e2timents d\u2019exploitation. Pour ces derniers, on passe par les fiches CEE agricoles, le PCAE et FranceAgriMer.",
    },
    {
      question: "Le bail rural permet-il de toucher la prime ?",
      answer:
        "Selon la fiche, oui : bail rural long avec accord \u00e9crit du propri\u00e9taire pour les travaux, ou b\u00e9n\u00e9ficiaire alternatif (propri\u00e9taire). Chaque cas est \u00e9tudi\u00e9 au moment du diagnostic.",
    },
  ],
  extraDocs: [
    "Récépissé MSA ou attestation d\u2019affiliation au régime agricole",
    "Notice technique du matériel (marque, modèle, rendement, conformité fiche)",
    "Plan ou cadastre du bâtiment concerné",
  ],
};

// ────────────────────────────────────────────────────────────────
// TRA — Transport (poids lourds, flottes, recharge)
// ────────────────────────────────────────────────────────────────

const TRA: SectorContext = {
  audience: {
    eyebrow: 'Vous êtes transporteur ou gestionnaire de flotte ?',
    title: 'CEE transport pour vos véhicules et infrastructures',
    description:
      "Les fiches TRA couvrent les bornes de recharge, le retrofit, le report modal et la formation \u00e9co-conduite. Cumulables avec le bonus \u00e9cologique et certaines aides r\u00e9gionales.",
    primaryCta: { label: 'Contacter un expert', href: '/contact?source=transport' },
    secondaryCta: { label: 'Tous les outils', href: '/outils' },
  },
  faq: [
    {
      question: 'Les bornes IRVE sont-elles éligibles aux CEE ?',
      answer:
        "Oui. Plusieurs fiches CEE (TRA-EQ) couvrent les bornes pour v\u00e9hicules \u00e9lectriques sur sites tertiaires, industriels et flottes. Cumulables avec le programme Advenir sous conditions.",
    },
    {
      question: 'Le retrofit poids lourds est-il finançable ?',
      answer:
        "Oui, via la fiche TRA-EQ-129 (et apparent\u00e9es). Il faut que le retrofit soit homologu\u00e9 par un installateur agr\u00e9\u00e9 et que le v\u00e9hicule remplisse les crit\u00e8res de la fiche.",
    },
  ],
  extraDocs: [
    'Carte grise des véhicules concernés (transport)',
    'Attestation d\u2019installation d\u2019un opérateur agréé IRVE',
  ],
};

// ────────────────────────────────────────────────────────────────
// RES — Réseau (chauffage urbain, raccordement)
// ────────────────────────────────────────────────────────────────

const RES: SectorContext = {
  audience: {
    eyebrow: 'Vous gérez un réseau ?',
    title: 'CEE réseaux et raccordements urbains',
    description:
      "Les fiches RES couvrent les réseaux de chaleur, le raccordement à un réseau classé R2 et la modernisation de centrales. Cumulables avec le Fonds chaleur ADEME.",
    primaryCta: { label: 'Contacter un expert', href: '/contact?source=reseau' },
    secondaryCta: { label: 'Tous les outils', href: '/outils' },
  },
  faq: [
    {
      question: 'Faut-il un réseau classé pour toucher la prime ?',
      answer:
        "Pour le raccordement d\u2019un b\u00e2timent \u00e0 un r\u00e9seau de chaleur, oui : le r\u00e9seau doit \u00eatre class\u00e9 R2 (plus de 50 % d\u2019\u00e9nergies renouvelables et de r\u00e9cup\u00e9ration). On confirme l\u2019\u00e9ligibilit\u00e9 lors du diagnostic.",
    },
  ],
  extraDocs: [
    'Convention de raccordement au réseau de chaleur',
    'Attestation de classement R2 du réseau (si applicable)',
  ],
};

// ────────────────────────────────────────────────────────────────
// EXPORT
// ────────────────────────────────────────────────────────────────

export const SECTOR_CONTEXT: Record<CeeSector, SectorContext> = {
  BAR,
  BAT,
  IND,
  AGRI,
  TRA,
  RES,
};

export function getSectorContext(sector: string | null | undefined): SectorContext {
  if (!sector) return RES;
  const key = sector.toUpperCase() as CeeSector;
  return SECTOR_CONTEXT[key] ?? RES;
}
